/**
 * feat-298 — Track › Lesson grouping for `/quiz-review` (spec §1, AT-1).
 *
 * A PURE function: the `learn-content` Lessons plus the `learn-tracks` payload in,
 * an ordered list of groups out. No React, no fetching, no DOM.
 *
 * Tracks are MANY-TO-MANY with Lessons. A Lesson renders in FULL exactly once —
 * under the first Track (in Track order) that lists it — and as a STUB at its
 * position in every later Track. Item content therefore never appears twice on
 * the page (and neither does any `data-item-key`), which feat-299 / feat-300
 * depend on.
 */
import { Audience, LearnTracksPayload, QuizUnit } from '../../../services/quizContentAdminService';

/** The group id used for the "Not in any Track" bucket. */
export const UNASSIGNED_GROUP_ID = 'none';

export interface FullPlacement {
  kind: 'full';
  lesson: QuizUnit;
  /** 1-based position within THIS group, stubs included (spec §1 Numbering). */
  number: number;
}

export interface StubPlacement {
  kind: 'stub';
  lesson: QuizUnit;
  number: number;
  /** The group that holds the full rendering. */
  ownerGroupId: string;
  ownerTitle: string;
}

export type LessonPlacement = FullPlacement | StubPlacement;

export interface LessonGroup {
  /** A track id, or {@link UNASSIGNED_GROUP_ID}. */
  id: string;
  kind: 'track' | 'unassigned';
  /** Track title (English in every mode), or '' for the unassigned group. */
  title: string;
  active: boolean;
  entries: LessonPlacement[];
  /** feat-302 — the Track's audience (absent for the unassigned group, and on a
   *  pre-feat-302 payload). */
  audience?: Audience;
}

export type GroupedCurriculum =
  | { mode: 'tracks'; groups: LessonGroup[] }
  /** No Track level: the tracks fetch failed, or the tenant has zero Tracks.
   *  feat-302: `numbers[i]` is `lessons[i]`'s full-curriculum number when View as
   *  has filtered the list (absent ⇒ `i + 1`, as before). */
  | { mode: 'flat'; lessons: QuizUnit[]; numbers?: number[] };

/**
 * Group `lessons` (in `learn-content` order) by the tenant's Tracks.
 *
 * - Tracks in payload order (`sort_order`); within a Track, that Track's own
 *   `lesson_ids` order.
 * - "Not in any Track" last, holding `unassigned_lesson_ids` — in
 *   `learn-content` order — plus any Lesson the payload mentions NOWHERE
 *   (never silently dropped; `console.warn`ed). Omitted when empty.
 * - A Track id pointing at a Lesson `learn-content` did not return is skipped
 *   (and does not take a number): there is nothing to render for it.
 * - `null` payload or zero Tracks ⇒ `flat`.
 *
 * `unassigned_lesson_ids` is used as the server computed it and is NOT
 * re-derived from `tracks` (see its doc comment on `LearnTracksPayload`).
 */
export function groupByTrack(
  lessons: QuizUnit[],
  tracks: LearnTracksPayload | null,
): GroupedCurriculum {
  if (!tracks || !tracks.tracks || tracks.tracks.length === 0) {
    return { mode: 'flat', lessons };
  }

  const byId = new Map(lessons.map((l) => [l.id, l] as const));
  /** lesson id → the group that renders it in full. */
  const owner = new Map<string, LessonGroup>();
  const groups: LessonGroup[] = [];

  const place = (group: LessonGroup, lesson: QuizUnit) => {
    const number = group.entries.length + 1;
    const first = owner.get(lesson.id);
    if (!first) {
      owner.set(lesson.id, group);
      group.entries.push({ kind: 'full', lesson, number });
    } else {
      group.entries.push({
        kind: 'stub',
        lesson,
        number,
        ownerGroupId: first.id,
        ownerTitle: first.title,
      });
    }
  };

  for (const track of tracks.tracks) {
    const group: LessonGroup = {
      id: track.id,
      kind: 'track',
      title: track.title,
      active: track.active,
      entries: [],
      audience: track.audience,
    };
    const seenHere = new Set<string>();
    for (const id of track.lesson_ids ?? []) {
      const lesson = byId.get(id);
      // A duplicate id inside one Track would render the Lesson twice in the
      // same group; the second is meaningless, so it is dropped.
      if (!lesson || seenHere.has(id)) continue;
      seenHere.add(id);
      place(group, lesson);
    }
    groups.push(group);
  }

  const unassigned: LessonGroup = {
    id: UNASSIGNED_GROUP_ID,
    kind: 'unassigned',
    title: '',
    active: true,
    entries: [],
  };
  const listedUnassigned = new Set(tracks.unassigned_lesson_ids ?? []);
  const orphans: string[] = [];
  for (const lesson of lessons) {
    if (listedUnassigned.has(lesson.id)) {
      if (!owner.has(lesson.id)) place(unassigned, lesson);
    } else if (!owner.has(lesson.id)) {
      orphans.push(lesson.id);
      place(unassigned, lesson);
    }
  }
  if (orphans.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      '[QuizContentReview] Lessons in no Track and not in unassigned_lesson_ids; ' +
        'showing them under "Not in any Track":',
      orphans,
    );
  }
  if (unassigned.entries.length > 0) groups.push(unassigned);

  return { mode: 'tracks', groups };
}
